import React from "react";
import LanguageSelect from "~/components/LanguageSelect";
import TagSelect from "~/components/TagSelect";
import TopicSelect from "~/components/TopicSelect";
import CountrySelect from "~/components/CountrySelect";
import DocTypeSelect, { Props as DocTypeSelectProps } from "~/components/DocumentTypeSelect";
import AccessLevelSelect from "~/components/AccessLevelSelect";
import SeriesSelect from "~/components/SeriesSelect";
import { RangedDatePicker, SingleDatePicker } from "~/components/PublicationDateSelect";
import ProjectNumberSelect from "~/components/ProjectNumberSelect";
import OwnerSelect from "~/components/OwnersSelect";
import OfficeSelect from "~/components/OfficeSelect";
import DocInstanceSelect from "~/components/DocInstanceSelect";
import { Custom, DateRange, DocDateFields, DocInstanceId, EditableDocPayload, OrganizationalUnitId } from "~/document";
import ExpirationDateSelect from "~/components/ExpirationDateSelect";
import { TagId } from "~/stores/tags";
import { DocSeriesId, Access } from "~/document";
import { TopicId } from "~/stores/topics";
import { CountryId } from "~/stores/country";
import { ProjectId } from "~/stores/projects";
import { OwnerId } from "~/stores/owners";
import { OfficeId } from "~/stores/offices";
import { DocTypeId } from "~/stores/documentTypes";
import DelayedTextInput from "~/components/BouncedInput";
import PDFOptionSelect from "~/components/PDFOptionSelect";
import { declString } from "~/util";
import styles from "./styles.module.scss";
import FormGroup from "~/components/FormGroup";
import FormHint from "~/components/FormHint";
import { Textarea } from "~/components/ui/textarea";
import moment from "moment";
import OrganizationalUnitSelect from "~/components/OrganizationalUnitSelect";

type CommonFormValue = Pick<EditableDocPayload, "title" | "protocol" | "number"> & {
  topics: Set<TopicId>;
  docTypes: Set<DocTypeId>;
  tags: TagId[];
  countries: CountryId[];
  meeting_codes?: string[];
  languages: string[];
  comment?: string;
  author?: OwnerId | OwnerId[];
  access: Access[];
  instance?: DocInstanceId;
  projects: ProjectId[];
  last_modified_user?: string;
  owners: OwnerId[];
  offices: OfficeId[];
  organizational_units: OrganizationalUnitId[];
  third_types: DocSeriesId[];
  abstract?: string;
  pdf_option?: number;
  custom?: Custom;
};

export type MultipleFormValue = CommonFormValue & {
  [P in DocDateFields]: DateRange | undefined;
};

export type SingleFormValue = CommonFormValue &
  Omit<
    {
      [P in DocDateFields]: Date | undefined;
    },
    "expiration_date"
  > & {
    expiration_date: Date | null | undefined;
  };
type CustomKeys =
  | "custom/comment"
  | "custom/decision"
  | "custom/summary"
  | "custom/disclaimer"
  | "custom/is_title_visible_to_public";

export type SingleOnChangeKeys = CustomKeys | keyof SingleFormValue;
export type MultipleOnChangeKeys = CustomKeys | keyof MultipleFormValue;

interface MultipleProps {
  multipleDocs: true;
  onChange: (key: MultipleOnChangeKeys, value: any) => void;
  value: MultipleFormValue;
  docTypeSelectProps?: Partial<DocTypeSelectProps>;
  formProps?: Partial<Record<keyof MultipleFormValue, any>>;
  typesIncludesGovernanceBody?: boolean;
}

export type SingleFormProps = Partial<Record<keyof SingleFormValue, any>>;
interface SingleProps {
  multipleDocs: false;
  onChange: (key: SingleOnChangeKeys, value: any) => void;
  value: SingleFormValue;
  docTypeSelectProps?: Partial<DocTypeSelectProps>;
  formProps?: SingleFormProps;
  typesIncludesGovernanceBody?: boolean;
}

const tooltipInfoMapping = {
  Title: "Official title of the document – to be entered in the language of the document.",
  Language: "Select the language of the document.",
  Topics:
    "Select from list of taxonomy topics\nMultiple topics from the whole taxonomy can be selected," +
    " but selection of at least one topic term is mandatory. The topics are displayed in a hierarchical format " +
    "of 3 levels.\\nSelect the topic term(s) from the whole taxonomy that best describe(s) the main content of the" +
    " document.\\nE.g. If you upload a new Gender Policy, select the entry-level topic 'Gender'. " +
    "If you upload guidelines on gender mainstreaming, select the sub-topic 'Gender mainstreaming'.\\n" +
    "When selecting sub-topics, do not also select the respective entry-level topics, as they are automatically captured.",
  "Document types":
    "Selection of one document type is mandatory; a second one is optional – to be used if " +
    "appropriate. Select the document type(s) under which most users would expect to find your document.",
  Tags:
    "Tags are free keywords that can be used to describe the main content of your document, if the taxonomy " +
    "is not specific enough. Do not create a tag for terms that are included in the lists of Topics, " +
    "Document types or Geographical coverage. If you create a tag that includes an acronym, write the full name " +
    "first and the acronym behind in parenthesis. Avoid using special characters in tags. " +
    "Create tags in English only.",
  "Geographical coverage":
    "If the content of a document relates to one or more " + "specific country/countries, indicate those.",
  "Author(s)":
    "For documents produced by WFP, enter the name(s) of the main person(s) in " +
    "WFP responsible for writing the document (First name Last name). For documents not produced by" +
    " WFP, enter the name(s) of the external author(s) and/or institutions who published " +
    "the document (e.g. Robert Brown, UNICEF)",
  Access:
    "Indicate if this document should be accessible only to WFP employees or to the public " +
    "(e.g. on the public website).",
  "Document symbol":
    "To be entered only if your document has an official symbol " +
    "(e.g. Executive Board documents, Circulars & Directives, Long-term agreements, Evaluations etc.). " +
    "Ensure consistency in the use of symbols.",
  "Project number":
    "When uploading project documents (EMOPs, PRROs or else), budget revisions, " +
    "standard project reports or project resource updates, enter the WINGS project number of the respective project.",
  Series: "If your document belongs to a defined series of regularly published documents, select the series.",
  "Publication date": "Date on which a document gets officially published.",
  "Release date":
    "Date on which a document gets released in GoDocs. You can set a release date in the " +
    "future (if a document should only be available from a certain date onwards), but not in the past.",
  "Expiration date": "Date on which a document gets archived, unless the expiration date gets extended.",
  "Document manager": "",
  Office: "Indicate the location of the office responsible for managing the document.",
  Division:
    "For documents uploaded at Headquarters, indicate the division unit that is responsible " +
    "for managing the document.",

  Notes: "",
  "PDF Options":
    "Indicate whether the document should appear 1. preferred in pdf format (Convert to PDF); " +
    "2. only in its native file format (Do Not Convert to PDF).",
  Abstract:
    "To be used as a teaser for longer, complex documents – to be entered in the language of " +
    "the document (max. 1,500 characters including spaces).",
};

const getOr = (obj: Record<string, any>, key: string, def: any) => {
  return obj[key] ?? def;
};

type Props = SingleProps | MultipleProps;

function isMultiple(props: Props): props is MultipleProps {
  return props.multipleDocs;
}

const pdfOptionMapping: any = {
  0: "PDF Not Allowed",
  1: "PDF Allowed",
  3: "PDF Preferred download type",
  5: "Only allow pdf format",
};

function DocumentForm(props: Props) {
  const { onChange, multipleDocs, docTypeSelectProps, typesIncludesGovernanceBody } = props;

  const formProps = props.formProps || {};
  const { value: { pdf_option = 1 } = {} } = props;

  return (
    <div className={`${styles.form} doc-form`}>
      <div className="mb-3 grid grid-cols-1 gap-6 md:grid-cols-2">
        <div>
          <FormGroup
            legendText={
              <>
                <span className={formProps.title?.invalid ? styles.errorLabel : ""}>Title{!multipleDocs && "*"}</span>
                <FormHint>{tooltipInfoMapping["Title"]}</FormHint>
              </>
            }
          >
            <DelayedTextInput
              {...getOr(formProps, "title", {})}
              value={props.value.title ?? ""}
              onChange={(ev) => onChange("title", ev)}
            />
          </FormGroup>
        </div>
        <div>
          <FormGroup
            legendText={
              <>
                <span className={formProps.languages?.invalid ? styles.errorLabel : ""}>
                  Language{!multipleDocs && "*"}
                </span>
                <FormHint>{tooltipInfoMapping["Language"]}</FormHint>
              </>
            }
            {...getOr(formProps, "languages", {})}
          >
            <LanguageSelect
              value={props.value.languages}
              onChange={(langs) => onChange("languages", langs)}
              selectProps={{
                isMulti: multipleDocs,
              }}
            />
          </FormGroup>
        </div>
      </div>
      <div className="border-b border-[#eee] my-3" />
      <div className="mb-3 grid grid-cols-1 gap-6 md:grid-cols-2">
        <div>
          <FormGroup
            legendText={
              <>
                <span className={formProps.topics?.invalid ? styles.errorLabel : ""}>Topics{!multipleDocs && "*"}</span>
                <FormHint>{tooltipInfoMapping["Topics"]}</FormHint>
              </>
            }
            {...getOr(formProps, "topics", {})}
          >
            <TopicSelect
              invalid={!!formProps.topics?.invalid}
              value={props.value.topics}
              onChange={(topics) => onChange("topics", topics)}
            />
          </FormGroup>
        </div>
        <div>
          <FormGroup
            legendText={
              <>
                <span className={formProps.docTypes?.invalid ? styles.errorLabel : ""}>
                  Document types{!multipleDocs && "*"}
                </span>
                <FormHint>{tooltipInfoMapping["Document types"]}</FormHint>
              </>
            }
            {...getOr(formProps, "docTypes", {})}
          >
            <DocTypeSelect
              invalid={!!formProps.docTypes?.invalid}
              value={props.value.docTypes}
              onChange={(value) => onChange("docTypes", value)}
              {...docTypeSelectProps}
            />
          </FormGroup>
        </div>
      </div>
      <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-2">
        <div>
          <FormGroup
            legendText={
              <>
                Tags
                <FormHint>{tooltipInfoMapping["Tags"]}</FormHint>
              </>
            }
            {...getOr(formProps, "tags", {})}
          >
            <TagSelect
              value={props.value.tags}
              tagValueKey={multipleDocs ? "id" : "label"}
              onChange={(tags) => {
                onChange("tags", tags);
              }}
              isCreatable
            />
          </FormGroup>
        </div>
        <div>
          <FormGroup
            legendText={
              <>
                Geographical coverage
                <FormHint>{tooltipInfoMapping["Geographical coverage"]}</FormHint>
              </>
            }
            {...getOr(formProps, "countries", {})}
          >
            <CountrySelect value={props.value.countries} onChange={(value) => onChange("countries", value)} />
          </FormGroup>
        </div>
      </div>
      <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-2">
        <div>
          <FormGroup
            legendText={
              <>
                <span className={formProps.author?.invalid ? styles.errorLabel : ""}>
                  Author(s){!multipleDocs && "*"}
                </span>
                <FormHint>{tooltipInfoMapping["Author(s)"]}</FormHint>
              </>
            }
          >
            <DelayedTextInput
              value={props.value.author}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange("author", e)}
              {...getOr(formProps, "author", {})}
            />
          </FormGroup>
        </div>
      </div>
      <div className="mb-6">
        <div>
          <FormGroup
            legendText={
              <>
                <span className={formProps.access?.invalid ? styles.errorLabel : ""}>Access{!multipleDocs && "*"}</span>
                <FormHint>{tooltipInfoMapping["Access"]}</FormHint>
              </>
            }
            {...getOr(formProps, "access", {})}
          >
            <AccessLevelSelect
              value={props.value.access}
              onChange={(value) => onChange("access", value)}
              selectProps={{ isMulti: multipleDocs }}
            />
          </FormGroup>
        </div>
      </div>
      <div className="mb-6">
        <div>
          <FormGroup
            legendText={
              <>
                Document symbol
                <FormHint>{tooltipInfoMapping["Document symbol"]}</FormHint>
              </>
            }
            {...getOr(formProps, "protocol", {})}
          >
            <DelayedTextInput
              value={props.value.number ?? ""}
              onChange={(e) => onChange("number", e)}
              placeholder="e.g. OE/2011/021"
            />
          </FormGroup>
        </div>
      </div>
      {typesIncludesGovernanceBody && !multipleDocs && (
        <div className="mb-6">
          <div>
            <FormGroup {...getOr(formProps, "instance", {})} legendText={<>Instance{!multipleDocs && "*"}</>}>
              <DocInstanceSelect
                value={props.value.instance}
                onChange={(value: DocInstanceId) => {
                  onChange("instance", value as any);
                }}
              />
            </FormGroup>
          </div>
        </div>
      )}
      <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-2">
        <div>
          <FormGroup
            {...getOr(formProps, "projects", {})}
            legendText={
              <>
                Project number
                <FormHint>{tooltipInfoMapping["Project number"]}</FormHint>
              </>
            }
          >
            <ProjectNumberSelect value={props.value.projects} onChange={(value) => onChange("projects", value)} />
          </FormGroup>
        </div>
        <div>
          <FormGroup
            {...getOr(formProps, "third_types", {})}
            legendText={
              <>
                Series
                <FormHint>{tooltipInfoMapping["Series"]}</FormHint>
              </>
            }
          >
            <SeriesSelect
              value={props.value.third_types ?? []}
              onChange={(value: DocSeriesId[]) => onChange("third_types", value)}
              selectProps={{ isMulti: multipleDocs }}
              canAddDocument={!multipleDocs}
            />
          </FormGroup>
        </div>
      </div>
      <div className="mb-6">
        <div>
          <FormGroup
            {...getOr(formProps, "publication_date", {})}
            legendText={
              <>
                Publication date
                <FormHint>{tooltipInfoMapping["Publication date"]}</FormHint>
              </>
            }
          >
            {isMultiple(props) ? (
              <RangedDatePicker
                value={props.value.publication_date}
                withDefaultValue={false}
                allowPast
                onChange={(value) => onChange("publication_date", value)}
                helperText="Select document publication date"
              />
            ) : (
              <SingleDatePicker
                value={props.value.publication_date}
                allowPast
                onChange={(value) => {
                  onChange("publication_date", value);
                }}
                helperText="Select document publication date"
              />
            )}
          </FormGroup>
        </div>
      </div>
      <div className="mb-6">
        <div>
          <FormGroup
            {...getOr(formProps, "release_date", {})}
            legendText={
              <>
                Release date
                <FormHint>{tooltipInfoMapping["Release date"]}</FormHint>
              </>
            }
          >
            {isMultiple(props) ? (
              <RangedDatePicker
                value={props.value.release_date}
                onChange={(value) => onChange("release_date", value)}
                helperText="Select document release date"
                allowPast
                withDefaultValue={false}
              />
            ) : (
              <SingleDatePicker
                allowPast={false}
                value={props.value.release_date || moment().toDate()}
                onChange={(value) => onChange("release_date", value)}
                helperText="Select document release date"
              />
            )}
          </FormGroup>
        </div>
      </div>
      <div className="mb-6">
        <div>
          <FormGroup
            {...getOr(formProps, "expiration_date", {})}
            legendText={
              <>
                <span className={formProps.expiration_date?.invalid ? styles.errorLabel : ""}>
                  Expiration date{!multipleDocs && "*"}
                </span>
                <FormHint>{tooltipInfoMapping["Expiration date"]}</FormHint>
              </>
            }
          >
            {isMultiple(props) ? (
              <RangedDatePicker
                onChange={(value) => onChange("expiration_date", value)}
                disabled={formProps["expiration_date"]?.disabled}
                helperText="Select expiration date range"
                value={props.value.expiration_date}
                allowPast
                withDefaultValue={false}
              />
            ) : (
              <ExpirationDateSelect
                value={props.value.expiration_date}
                onChange={(value) => onChange("expiration_date", value)}
                disabled={formProps["expiration_date"]?.disabled}
                startDate={formProps["expiration_date"]?.startDate}
              />
            )}
          </FormGroup>
        </div>
      </div>
      {!multipleDocs && (
        <div className="mb-6">
          <div>
            <FormGroup
              legendText={
                <>
                  Abstract
                  <FormHint>{tooltipInfoMapping["Abstract"]}</FormHint>
                </>
              }
              {...getOr(formProps, "protocol", {})}
            >
              <Textarea
                onChange={(e) => onChange("abstract", e.target.value)}
                name="abstract"
                value={props.value.abstract ?? ""}
              />
            </FormGroup>
          </div>
        </div>
      )}
      <div className="mb-6">
        {multipleDocs && (
          <div>
            <FormGroup {...getOr(formProps, "owners", {})} legendText={declString("Document manager", multipleDocs)}>
              <OwnerSelect
                value={props.value.owners}
                onChange={(value) => onChange("owners", value)}
                selectProps={{
                  isMulti: multipleDocs,
                }}
              />
            </FormGroup>
          </div>
        )}
        {multipleDocs && (
          <div className="mb-6">
            <FormGroup legendText="Last updated by">
              <DelayedTextInput
                value={props.value.last_modified_user ?? ""}
                onChange={(value) => onChange("last_modified_user", value)}
              />
              <div className="mt-1 text-xs text-muted-foreground">Enter the username of the user who last updated the document</div>
            </FormGroup>
          </div>
        )}
        <div>
          <FormGroup
            {...getOr(formProps, "offices", {})}
            legendText={
              <>
                {declString("Office", multipleDocs)}
                <FormHint>{tooltipInfoMapping["Office"]}</FormHint>
              </>
            }
          >
            <OfficeSelect
              value={props.value.offices}
              onChange={(value) => onChange("offices", value)}
              selectProps={{
                isMulti: multipleDocs,
              }}
            />
          </FormGroup>
        </div>
      </div>
      <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-2">
        <div>
          <FormGroup {...getOr(formProps, "organizational_unit", {})} legendText={<>Organizational Unit</>}>
            <div className="mt-2">
              <OrganizationalUnitSelect
                value={props.value.organizational_units}
                onlyActive={!multipleDocs}
                onChange={(value) => onChange("organizational_units", value)}
                selectProps={{
                  isMulti: multipleDocs,
                }}
              />
            </div>
          </FormGroup>
        </div>
      </div>
      {!multipleDocs && (
        <>
          <div className="mb-6">
            <div>
              <FormGroup legendText="Notes" {...getOr(formProps, "protocol", {})}>
                <Textarea
                  onChange={(e) => onChange("comment", e.target.value)}
                  value={props.value.comment ?? ""}
                  name="comment"
                />
              </FormGroup>
            </div>
          </div>
          <div className="mb-6">
            <div>
              <FormGroup legendText="PDF Option" {...getOr(formProps, "protocol", {})}>
                <PDFOptionSelect
                  value={pdfOptionMapping[pdf_option]}
                  onChange={(value) => onChange("pdf_option", getPDFOption(value))}
                />
              </FormGroup>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const getPDFOption = (val: string | null | number) => {
  return Object.keys(pdfOptionMapping).find((key) => pdfOptionMapping[key] === val);
};
export default DocumentForm;
